import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { isAdminEmail } from '../../lib/adminEmails'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sentEmail, setSentEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { sendMagicCode, verifyMagicCode } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // If the person was sent here from a specific protected page (e.g. deep
  // link to a dashboard sub-page), return them there — that's always the
  // more useful destination. Otherwise, this one sign-in page serves both
  // admin and clients, so send each to the dashboard that's actually theirs
  // rather than defaulting everyone to the client dashboard.
  const goAfterAuth = (signedInEmail?: string) => {
    const from = (location.state as any)?.from?.pathname
    if (from) {
      navigate(from, { replace: true })
      return
    }
    navigate(isAdminEmail(signedInEmail) ? '/admin/dashboard' : '/dashboard', { replace: true })
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const normalized = email.trim().toLowerCase()
      // No existence pre-check: InstantDB creates the account automatically
      // on first verified code, the same way the Sign Up page already
      // works. Checking first would mean an unauthenticated endpoint that
      // reveals which emails have accounts — this avoids that entirely.
      // A brand-new visitor who lands here instead of Sign Up still gets a
      // working account; their name defaults from their email and they can
      // set it properly afterward in Settings.
      const { error: sendError } = await sendMagicCode(normalized)
      if (sendError) setError(sendError.message)
      else setSentEmail(normalized)
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const pendingName = sessionStorage.getItem('pending_signup_name') || undefined
      const { error: verifyError } = await verifyMagicCode(sentEmail, code, pendingName)
      if (verifyError) {
        setError(verifyError.message)
      } else {
        try {
          sessionStorage.removeItem('pending_signup_name')
          sessionStorage.removeItem('pending_signup_email')
        } catch {
          /* ignore */
        }
        goAfterAuth(sentEmail)
      }
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/40 transition-all duration-300'

  return (
    <div className="relative min-h-[calc(100vh-8rem)] overflow-hidden bg-ink texture-dots">
      {/* Mobile / tablet: full-bleed photo behind the centered form (unchanged from the approved look) */}
      <div
        className="absolute inset-0 lg:hidden bg-cover bg-[65%_12%] sm:bg-[85%_10%]"
        style={{ backgroundImage: "url('/images/cassie-login-hero.webp')" }}
        aria-hidden
      />
      <div className="absolute inset-0 lg:hidden bg-brand-navy/80" aria-hidden />

      {/* Desktop: two clear zones — solid navy for the form on the left, the photo fully visible on the right */}
      <div className="hidden lg:flex absolute inset-0">
        <div className="w-[52%] bg-brand-navy" aria-hidden />
        <div className="relative flex-1 overflow-hidden">
          <div
            className="absolute inset-0 bg-no-repeat bg-contain bg-bottom"
            style={{ backgroundImage: "url('/images/cassie-login-hero.webp')" }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-brand-navy/10" aria-hidden />
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-brand-navy to-transparent" aria-hidden />
        </div>
      </div>

      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-brand/15 rounded-full blur-3xl pointer-events-none" aria-hidden />
      <div className="absolute -bottom-40 -right-32 w-[380px] h-[380px] bg-gold/5 rounded-full blur-3xl pointer-events-none" aria-hidden />

      <div className="relative z-10 min-h-[calc(100vh-8rem)] flex items-center justify-center px-4 py-16 sm:px-6 sm:py-20 lg:w-[52%] lg:justify-center lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-10">
          <img
            src="/images/logo-light.webp"
            alt="Fit by Cassie"
            className="h-14 sm:h-16 w-auto mx-auto mb-8"
          />
          <div className="w-16 h-0.5 rounded-full bg-brand-blush mx-auto mb-8" />
          <h1 className="font-display text-3xl sm:text-4xl text-brand-ivory leading-snug mb-4">
            Welcome back <em className="italic text-brand-blush">home</em>.
          </h1>
          <p className="text-brand-cream/80 leading-relaxed max-w-sm mx-auto mb-3">
            Every time you show up for yourself, you're building something stronger.
          </p>
          <p className="text-gray-400 leading-relaxed max-w-sm mx-auto">
            {sentEmail
              ? `Enter the code we sent to ${sentEmail}.`
              : "Enter your email and we'll send you a one-time sign-in code."}
          </p>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-400/30 text-red-300 px-4 py-3 rounded-xl mb-6 text-sm"
          >
            {error}
          </motion.div>
        )}

        {!sentEmail ? (
          <form onSubmit={handleSendCode} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="you@example.com"
              />
            </div>

            <button type="submit" disabled={loading} className="w-full btn-gold !py-4">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ink" />
              ) : (
                <>
                  Send Code
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} className="space-y-5">
            <div>
              <label htmlFor="code" className="block text-sm font-semibold text-gray-300 mb-2">
                Verification code
              </label>
              <input
                id="code"
                type="text"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={inputClass}
                placeholder="123456"
                autoFocus
              />
            </div>
            <button type="submit" disabled={loading} className="w-full btn-gold !py-4">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ink" />
              ) : (
                <>
                  Verify & Sign In
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setSentEmail('')
                setCode('')
              }}
              className="w-full text-sm text-gray-400 hover:text-gold-light"
            >
              Use a different email
            </button>
          </form>
        )}

        <div className="mt-8 pt-8 border-t border-white/10 text-center">
          <p className="text-gray-400 mb-6">
            New to Fit by Cassie?{' '}
            <Link
              to="/signup"
              className="text-gold-light font-semibold hover:text-gold transition-colors"
            >
              Create an account
            </Link>
          </p>
          <p className="text-xs font-semibold uppercase tracking-widest-xl text-gray-500">
            Personal Training · Nutrition · Accountability
          </p>
        </div>
      </motion.div>
      </div>
    </div>
  )
}

export default Login
